import React from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Slider from '@mui/material/Slider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import Paper from '@mui/material/Paper'
import FilterAltIcon from '@mui/icons-material/FilterAlt'

import { ProfileType, ProfileTypeString } from '../../types/enums/profileType'
import { ProfileMetadataFilter } from '../../types/profileMetadataFilter'
import SearchField from '../logs/logSearchField'
import CheckboxGroup from './checkboxGroup'
import TooltipIconButton from '../fields/tooltipIconButton'
import { isPounds } from '../../utils/localStorageUtils'
import { convertToWeightScale } from '../../utils/weightUtils'

const DEFAULT_WEIGHT_VALUES = [50, 200]

interface ProfileLibraryFiltersProps {
  filterConfig: Array<ProfileMetadataFilter>
  onFiltersChanged: (
    searchFilters: Array<number>,
    profileTypeFilter: Array<ProfileType>,
    weightFilter: Array<number>,
    searchString: string
  ) => void
}

const ProfileLibraryFilters = (props: ProfileLibraryFiltersProps): JSX.Element => {
  const { t } = useTranslation()

  const [isFilterMenuOpen, setIsFilterMenuOpen] = React.useState<boolean>(false)
  const [isFiltered, setIsFiltered] = React.useState<boolean>(false)
  const [searchString, setSearchString] = React.useState<string>('')
  const [sliderValue, setSliderValue] = React.useState<Array<number>>(DEFAULT_WEIGHT_VALUES)
  const [selectedProfileTypes, setSelectedProfileTypes] = React.useState<Array<ProfileType>>([])
  const [selectedFilters, setSelectedFilters] = React.useState<Array<number>>([])

  const isDefaultWeightFilter = (): boolean => {
    return sliderValue[0] === DEFAULT_WEIGHT_VALUES[0] && sliderValue[1] === DEFAULT_WEIGHT_VALUES[1]
  }

  const getSliderTickLabel = (value: number): { value: number; label: JSX.Element } => {
    return {
      value: value,
      label: (
        <Stack alignItems="center">
          <div>{`${value}g`}</div>
          {isPounds() && <div>{`${convertToWeightScale(value).toFixed(2)}oz`}</div>}
        </Stack>
      ),
    }
  }

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="flex-start" mt={2}>
        <Stack spacing={0.25}>
          <SearchField
            placeholder={t('profilelibrary.searchforprofile')}
            onSearch={(newSearchString: string) => {
              if (searchString !== newSearchString) {
                setSearchString(newSearchString)
                const weightFilter = isDefaultWeightFilter() ? [] : sliderValue
                props.onFiltersChanged(selectedFilters, selectedProfileTypes, weightFilter, newSearchString)
              }
            }}
          />
        </Stack>
        <TooltipIconButton
          onClick={() => setIsFilterMenuOpen((isOpen) => !isOpen)}
          tooltip={isFilterMenuOpen ? t('profilelibrary.hidefilters') : t('profilelibrary.showfilters')}
          icon={<FilterAltIcon />}
          isToggled={isFiltered}
        />
      </Stack>
      <Collapse in={isFilterMenuOpen}>
        <Paper elevation={4}>
          <Stack spacing={2} sx={{ p: 2 }}>
            <Typography variant="h6">{t('profilelibrary.filterresultsby')}</Typography>
            <Stack>
              <Typography variant="subtitle2">{t('profilelibrary.weightfilter')}</Typography>
              <Stack sx={{ ml: 1.25, mr: 2, mb: 2 }}>
                <Slider
                  valueLabelFormat={(number, index) => {
                    const poundsString = isPounds() ? `(${convertToWeightScale(number).toFixed(2)}oz)` : ''
                    return `${index === 0 ? 'min' : 'max'} ${number}g ${poundsString}`
                  }}
                  min={50}
                  max={200}
                  step={10}
                  valueLabelDisplay="auto"
                  getAriaLabel={() => t('profilelibrary.weightrange')}
                  value={sliderValue}
                  marks={[
                    getSliderTickLabel(50),
                    getSliderTickLabel(100),
                    getSliderTickLabel(150),
                    getSliderTickLabel(200),
                  ]}
                  onChange={(_event: Event | React.SyntheticEvent, newValue: number | number[]) => {
                    setSliderValue(newValue as number[])
                  }}
                />
              </Stack>
            </Stack>
            <Stack direction="row" flexWrap="wrap">
              <CheckboxGroup
                values={[
                  { id: ProfileType.BT, label: t(ProfileTypeString[ProfileType.BT]) },
                  { id: ProfileType.ET, label: t(ProfileTypeString[ProfileType.ET]) },
                  { id: ProfileType.POWER, label: t(ProfileTypeString[ProfileType.POWER]) },
                  { id: ProfileType.INLET_TEMP, label: t(ProfileTypeString[ProfileType.INLET_TEMP]) },
                  { id: ProfileType.BT_INLET, label: t(ProfileTypeString[ProfileType.BT_INLET]) },
                  { id: ProfileType.BT_POWER, label: t(ProfileTypeString[ProfileType.BT_POWER]) },
                ]}
                header={t('profilelibrary.profiletype')}
                checkedValues={selectedProfileTypes}
                onChangeValue={(value, isChecked) => {
                  setSelectedProfileTypes((oldProfileTypes) => {
                    return isChecked ? [...oldProfileTypes, value] : oldProfileTypes.filter((y) => y !== value)
                  })
                }}
              />
              {props.filterConfig.map((filter) => {
                return (
                  <CheckboxGroup
                    key={filter.id}
                    values={filter.filter_values}
                    header={filter.name}
                    checkedValues={selectedFilters}
                    onChangeValue={(value, isChecked) => {
                      setSelectedFilters((oldFilters) => {
                        return isChecked ? [...oldFilters, value] : oldFilters.filter((y) => y !== value)
                      })
                    }}
                  />
                )
              })}
            </Stack>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => {
                  const isEmptyFilter =
                    selectedFilters.length === 0 && selectedProfileTypes.length === 0 && isDefaultWeightFilter()

                  const weightFilter = isDefaultWeightFilter() ? [] : sliderValue
                  props.onFiltersChanged(selectedFilters, selectedProfileTypes, weightFilter, searchString)
                  setIsFiltered(!isEmptyFilter)
                  setIsFilterMenuOpen(false)
                }}
              >
                {t('profilelibrary.applyfilters')}
              </Button>
              <Button
                onClick={() => {
                  setSliderValue(DEFAULT_WEIGHT_VALUES)
                  setSelectedProfileTypes([])
                  setSelectedFilters([])
                  setIsFiltered(false)
                  setIsFilterMenuOpen(false)
                  props.onFiltersChanged([], [], [], searchString)
                }}
              >
                {t('profilelibrary.clearfilters')}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Collapse>
    </>
  )
}

export default ProfileLibraryFilters
